import {useTranslation} from 'react-i18next';

import TableActions from '@/components/data-table/TableActions';
import TableNotFound from '@/components/data-table/TableNotFound';
import TablePagination from '@/components/data-table/TablePagination';
import TableSearch from '@/components/data-table/TableSearch';
import {useTable} from '@/components/data-table/useTable';
import {Button} from '@/components/ui/button';
import {InputLabel} from '@/components/ui/input';
import {Switch} from '@/components/ui/switch';
import {Typography} from '@/components/ui/typography';

import type {ResidentWithOccupiedRoom} from '../utils/getResidentsWithRoom';
import {exportResidentColumns} from '../utils/residentColumns';
import ExportResidentsListView from './ExportResidentsListView';
import ExportResidentsPropertiesToggle from './ExportResidentsPropertiesToggle';

export type ExportResidentsTableProps = {
  residents: ResidentWithOccupiedRoom[];
};

const tooglableColumnsIds = exportResidentColumns.reduce<string[]>(
  (ids, column) =>
    column?.meta?.enableExportPreviewToogle ? [...ids, column.id!] : ids,
  []
);

const initialColumnVisibility = tooglableColumnsIds.reduce(
  (visibleColumns, id) => {
    return {
      ...visibleColumns,
      [id]: false,
    };
  },
  {}
);

const ExportResidentsTable = ({residents}: ExportResidentsTableProps) => {
  const {t} = useTranslation();

  const table = useTable({
    columns: exportResidentColumns,
    data: residents,
    initialState: {
      pagination: {
        pageSize: 50,
      },
      columnVisibility: initialColumnVisibility,
    },
  });

  const handleCopy = () => {
    const selectedRows = table.getSelectedRowModel().rows;

    const text = selectedRows
      .map(row => {
        return table
          .getAllColumns()
          .filter(
            column =>
              column.getIsVisible() && column.columnDef.meta?.enableExport
          )
          .map(column => row.getValue(column.id))
          .join(' ');
      })
      .join('\n');

    navigator.clipboard.writeText(text);
  };

  const isPropertiesColumnSelected = Object.entries(
    table.getState().columnVisibility
  ).some(([id, isVisible]) => tooglableColumnsIds.includes(id) && isVisible);

  return (
    <>
      <ExportResidentsPropertiesToggle table={table} />
      <div className="mt-4 flex flex-col gap-5">
        <Typography variant="h3" asChild>
          <h3>{t('Residents to export')}</h3>
        </Typography>
        <div className="flex flex-row items-center gap-2">
          <div className="grow">
            <TableSearch
              table={table}
              placeholder={t('Resident name or room number')}
            />
          </div>
          <Button
            type="button"
            variant="primary"
            disabled={
              !table.getSelectedRowModel().rows.length ||
              !isPropertiesColumnSelected
            }
            onClick={handleCopy}
          >
            {t('Copy')}
          </Button>
        </div>
        <TableActions table={table}>
          <div className="mr-3 flex items-center space-x-2">
            <InputLabel
              htmlFor="residents-export-all"
              className="text-xs text-marian-800"
            >
              {t('Export all')}
            </InputLabel>
            <Switch
              id="residents-export-all"
              checked={table.getIsAllRowsSelected()}
              onCheckedChange={() => {
                table.getToggleAllRowsSelectedHandler()({
                  target: {checked: !table.getIsAllRowsSelected()},
                });
              }}
            />
          </div>
          <div className="mr-3 flex items-center space-x-2">
            <InputLabel
              htmlFor="residents-expand"
              className="text-xs text-marian-800"
            >
              {t('Show properties to export')}
            </InputLabel>
            <Switch
              id="residents-expand"
              checked={table.getIsAllRowsExpanded()}
              onCheckedChange={() => {
                table.getToggleAllRowsExpandedHandler()({
                  target: {checked: !table.getIsAllRowsExpanded()},
                });
              }}
            />
          </div>
        </TableActions>
        <ExportResidentsListView table={table} />
        <TableNotFound table={table} />
        <TablePagination table={table} />
      </div>
    </>
  );
};

export default ExportResidentsTable;
